import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import DashboardShell from '../../components/DashboardShell/DashboardShell';
import PageHeader from '../../components/PageHeader/PageHeader';
import WalletBadge from '../../components/WalletBadge/WalletBadge';
import styles from './Support.module.css';
import axiosClient from '../../api/axiosClient';

interface Ticket {
  id: string;
  subject: string;
  message: string;
  status: 'open' | 'in_progress' | 'resolved';
  admin_reply: string | null;
  created_at: string;
}

const STATUS_COLORS: Record<string, string> = {
  open: '#e8a86a',
  in_progress: '#1d60f6',
  resolved: '#5cb87d',
};

const Support: React.FC = () => {
  // Links such as /dashboard/support?subject=... pre-fill the form (used by the "request a change" links).
  const prefill = new URLSearchParams(window.location.search);
  const [subject, setSubject] = useState(prefill.get('subject') || '');
  const [message, setMessage] = useState(prefill.get('message') || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);

  const loadTickets = () => {
    axiosClient.get('/support/mine').then(({ data }) => setTickets(data.tickets)).finally(() => setLoading(false));
  };

  useEffect(() => { loadTickets(); }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      await axiosClient.post('/support', { subject, message });
      setSubject(''); setMessage('');
      toast.success('Ticket submitted — our team will get back to you.');
      loadTickets();
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Something went wrong.';
      setError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardShell>
      <PageHeader title="Support" subtitle="Raise a query about KYC, funds, referrals, or anything else — our team will respond here." action={<WalletBadge />} />
      <div className={styles.page}>
        <form className={styles.card} onSubmit={handleSubmit}>
          <div className={styles.field}>
            <label>Subject</label>
            <input required value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="What's this about?" />
          </div>
          <div className={styles.field}>
            <label>Message</label>
            <textarea required rows={4} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Describe your issue or question" />
          </div>
          {error && <div className={styles.errorNote}>{error}</div>}
          <button type="submit" className={styles.submitBtn} disabled={saving}>{saving ? 'Sending…' : 'Submit Ticket'}</button>
        </form>

        <h2 className={styles.sectionTitle}>Your tickets</h2>
        {loading && <div className={styles.muted}>Loading…</div>}
        {!loading && tickets.length === 0 && <div className={styles.muted}>No tickets yet.</div>}
        {!loading && tickets.map((t) => (
          <div key={t.id} className={styles.ticketCard}>
            <div className={styles.ticketTop}>
              <b>{t.subject}</b>
              <span className={styles.statusPill} style={{ color: STATUS_COLORS[t.status], borderColor: STATUS_COLORS[t.status] }}>
                {t.status.replace('_', ' ')}
              </span>
            </div>
            <div className={styles.ticketMeta}>{new Date(t.created_at).toLocaleString()}</div>
            <p className={styles.ticketMsg}>{t.message}</p>
            {t.admin_reply && (
              <div className={styles.replyBox}>
                <b>Team reply:</b>
                <p>{t.admin_reply}</p>
              </div>
            )}
          </div>
        ))}
      </div>
    </DashboardShell>
  );
};

export default Support;